import api from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type {
  Expense,
  ExpenseCategory,
  ExpensePayment,
  ExpenseStatusEvent,
  Particular,
  ParticularStatusEvent,
  InvoicePreview,
  JobInvoice,
  JobInvoicePayment,
  PaginatedResponse,
  ApiResponse,
} from '@/types'

const unwrapPayload = <T>(payload?: ApiResponse<T> | T | null): T | null => {
  if (!payload) {
    return null
  }

  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    return (payload as ApiResponse<T>).data ?? null
  }

  return payload as T
}

const normalizePaginated = <T>(payload?: ApiResponse<PaginatedResponse<T>> | PaginatedResponse<T> | null): PaginatedResponse<T> => {
  if (!payload) {
    return { count: 0, next: null, previous: null, results: [] }
  }

  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    return (payload as ApiResponse<PaginatedResponse<T>>).data ?? { count: 0, next: null, previous: null, results: [] }
  }

  return payload as PaginatedResponse<T>
}

const publicPaymentRequestOptions = {
  skipAuthRedirectOn401: true,
  skipPostIdHeader: true,
  skipServiceInstanceId: true,
} as any

export interface FinanceFilters {
  page?: number
  page_size?: number
  service_instance_id?: string | number | null
  invoice_id?: string | number
  job_id?: string
  assignment_id?: string
  worker_id?: string
  responsible_worker_id?: string
  category_id?: string
  supplier_id?: string
  type?: string
  status?: string
  payment_status?: string
  search?: string
  date_from?: string
  date_to?: string
  billable?: boolean
  reimbursable?: boolean
  paid_by_company?: boolean
  to_be_purchased?: boolean
  purchase_status?: string
}

export interface CompanyPaymentRecordPayload {
  worker_id: string | number
  job_id?: string | number
  amount: number
  payment_mode: 'cash' | 'bank' | 'upi' | 'cheque' | 'other'
  payment_date?: string
  reference?: string
  notes?: string
}

export interface WorkerPaymentRecordPayload {
  invoice_id?: string | number | null
  job_id?: string | number | null
  worker_id?: string | number | null
  wallet_id?: string | number | null
  amount: number
  payment_method?: ExpensePayment['payment_mode']
  paid_on?: string
  payment_ref?: string
  transaction_id?: string
  remarks?: string
  additional_details?: string
}

export interface WorkerInvoiceGenerationRun {
  id: string | number
  service_instance_id?: string | number | null
  trigger_source?: string | null
  status: 'running' | 'success' | 'partial' | 'failed'
  cutoff_time?: string | null
  window_start?: string | null
  window_end?: string | null
  started_at?: string | null
  finished_at?: string | null
  jobs_scanned: number
  jobs_attempted: number
  jobs_succeeded: number
  jobs_failed: number
  invoices_created: number
  invoices_updated: number
  error_message?: string | null
  result_summary?: Record<string, unknown>
}

const normalizeExpensePayload = (expenseData: Partial<Expense> & { worker_id?: string | number | null }) => {
  const { job_id, job, category, supplier, worker, worker_id, ...rest } = expenseData
  const resolvedWorker = worker ?? worker_id

  return {
    ...rest,
    ...(job_id ? { job: job_id } : typeof job === 'string' || typeof job === 'number' ? { job } : {}),
    ...(category ? { category } : {}),
    ...(supplier ? { supplier } : {}),
    ...(resolvedWorker !== undefined && resolvedWorker !== null && resolvedWorker !== ''
      ? { worker: resolvedWorker }
      : {}),
  }
}

const normalizeParticularPayload = (particularData: Partial<Particular>) => {
  const { job_id, job, ...rest } = particularData
  const resolvedJobId = job_id ?? (typeof job === 'string' || typeof job === 'number' ? job : undefined)

  return {
    ...rest,
    ...(resolvedJobId !== undefined && resolvedJobId !== null ? { job_id: resolvedJobId } : {}),
    ...(resolvedJobId !== undefined && resolvedJobId !== null ? { job: resolvedJobId } : {}),
  }
}

const normalizeExpenseCategoryPayload = (categoryData: Partial<ExpenseCategory>) => {
  const { slug, category_key, ...rest } = categoryData

  return {
    ...rest,
    ...(slug ? { slug } : {}),
    ...(category_key ? { category_key } : {}),
  }
}

const normalizeExpensePaymentPayload = (paymentData: Partial<ExpensePayment> & { expense_id?: string | number }) => {
  const { expense_id, ...rest } = paymentData

  return {
    ...rest,
    ...(expense_id ? { expense: expense_id } : {}),
  }
}

export const financeApi = {
  // Expenses
  getExpenses: async (filters: FinanceFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.job_id) params.append('jobId', filters.job_id)
    if (filters.category_id) params.append('category_id', filters.category_id)
    if (filters.supplier_id) params.append('supplier_id', filters.supplier_id)
    if (filters.status) params.append('status', filters.status)
    if (filters.payment_status) params.append('payment_status', filters.payment_status)
    if (filters.date_from) params.append('incurred_at_after', filters.date_from)
    if (filters.date_to) params.append('incurred_at_before', filters.date_to)
    if (filters.billable !== undefined) params.append('billable', String(filters.billable))
    if (filters.reimbursable !== undefined) params.append('reimbursable', String(filters.reimbursable))
    if (filters.paid_by_company !== undefined) params.append('paid_by_company', String(filters.paid_by_company))

    const { data } = await api.get<ApiResponse<PaginatedResponse<Expense>> | PaginatedResponse<Expense>>(API_PATHS.finance.expenses, { params })
    return 'data' in data ? data.data : data
  },

  getExpense: async (id: string) => {
    const { data } = await api.get<Expense>(API_PATHS.finance.expenseDetail(id))
    return data
  },

  createExpense: async (expenseData: Partial<Expense>) => {
    const { data } = await api.post<ApiResponse<Expense>>(
      API_PATHS.finance.createExpense,
      normalizeExpensePayload(expenseData),
    )
    return data.data
  },

  updateExpense: async (id: string, expenseData: Partial<Expense>) => {
    const { data } = await api.patch<ApiResponse<Expense>>(
      API_PATHS.finance.updateExpense(id),
      normalizeExpensePayload(expenseData),
    )
    return data.data
  },

  setExpenseStatus: async (id: string, status: NonNullable<Expense['status']>, notes?: string) => {
    const { data } = await api.post<ApiResponse<Expense>>(
      API_PATHS.finance.setExpenseStatus(id),
      { status, ...(notes ? { notes } : {}) },
    )
    return data.data
  },

  getExpenseStatusTimeline: async (id: string) => {
    const { data } = await api.get<ApiResponse<ExpenseStatusEvent[]> | ExpenseStatusEvent[]>(
      API_PATHS.finance.expenseStatusTimeline(id),
    )
    return unwrapPayload<ExpenseStatusEvent[]>(data) ?? []
  },

  deleteExpense: async (id: string) => {
    await api.delete(API_PATHS.finance.deleteExpense(id))
  },

  uploadExpenseReceipt: async (expenseId: string, file: File) => {
    const formData = new FormData()
    formData.append('receipt', file)

    const { data } = await api.post<ApiResponse<Expense> | Expense>(
      API_PATHS.finance.uploadExpenseReceipt(expenseId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )

    return 'data' in data ? data.data : data
  },

  deleteExpenseReceipt: async (expenseId: string) => {
    await api.delete(API_PATHS.finance.deleteExpenseReceipt(expenseId))
  },

  getExpenseCategories: async (
    options: boolean | { includeInactive?: boolean; categoryKey?: 'exp' | 'inv' } = false,
  ) => {
    const normalized = typeof options === 'boolean' ? { includeInactive: options } : options
    const params: Record<string, string> = {}

    if (normalized.includeInactive) {
      params.include_inactive = '1'
    }
    if (normalized.categoryKey) {
      params.category_key = normalized.categoryKey
    }

    const { data } = await api.get<ApiResponse<PaginatedResponse<ExpenseCategory>> | PaginatedResponse<ExpenseCategory> | ExpenseCategory[]>(API_PATHS.finance.expenseCategories, {
      params: Object.keys(params).length ? params : undefined,
    })
    const payload = 'data' in data ? data.data : data
    return Array.isArray(payload) ? payload : payload.results ?? []
  },

  createExpenseCategory: async (categoryData: Partial<ExpenseCategory>) => {
    const { data } = await api.post<ApiResponse<ExpenseCategory>>(
      API_PATHS.finance.createExpenseCategory,
      normalizeExpenseCategoryPayload(categoryData),
    )
    return data.data
  },

  updateExpenseCategory: async (id: string, categoryData: Partial<ExpenseCategory>) => {
    const { data } = await api.put<ApiResponse<ExpenseCategory>>(
      API_PATHS.finance.updateExpenseCategory(id),
      normalizeExpenseCategoryPayload(categoryData),
    )
    return data.data
  },

  deleteExpenseCategory: async (id: string) => {
    await api.delete(API_PATHS.finance.deleteExpenseCategory(id))
  },

  getExpensePayments: async (expenseId: string) => {
    const params = new URLSearchParams()
    params.append('expenseId', expenseId)
    params.append('page_size', '100')

    const { data } = await api.get<ApiResponse<PaginatedResponse<ExpensePayment>> | PaginatedResponse<ExpensePayment>>(API_PATHS.finance.expensePayments, { params })
    return 'data' in data ? data.data : data
  },

  createExpensePayment: async (paymentData: Partial<ExpensePayment> & { expense_id: string | number }) => {
    const { data } = await api.post<ApiResponse<ExpensePayment>>(
      API_PATHS.finance.createExpensePayment,
      normalizeExpensePaymentPayload(paymentData),
    )
    return data.data
  },

  updateExpensePayment: async (id: string, paymentData: Partial<ExpensePayment> & { expense_id?: string | number }) => {
    const { data } = await api.put<ApiResponse<ExpensePayment>>(
      API_PATHS.finance.updateExpensePayment(id),
      normalizeExpensePaymentPayload(paymentData),
    )
    return data.data
  },

  deleteExpensePayment: async (id: string) => {
    await api.delete(API_PATHS.finance.deleteExpensePayment(id))
  },

  // Particulars
  getParticulars: async (filters: FinanceFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.job_id) params.append('jobId', filters.job_id)
    if (filters.category_id) params.append('category_id', filters.category_id)
    if (filters.responsible_worker_id) params.append('responsible_worker_id', filters.responsible_worker_id)
    if (filters.date_from) params.append('purchase_by_after', filters.date_from)
    if (filters.date_to) params.append('purchase_by_before', filters.date_to)
    if (filters.to_be_purchased !== undefined) params.append('to_be_purchased', String(filters.to_be_purchased))
    if (filters.purchase_status) params.append('purchase_status', filters.purchase_status)

    const { data } = await api.get<ApiResponse<PaginatedResponse<Particular>> | PaginatedResponse<Particular>>(API_PATHS.finance.particulars, { params })
    return 'data' in data ? data.data : data
  },

  getParticular: async (id: string) => {
    const { data } = await api.get<ApiResponse<Particular> | Particular>(API_PATHS.finance.particularDetail(id))
    return unwrapPayload<Particular>(data)
  },

  createParticular: async (particularData: Partial<Particular>) => {
    const { data } = await api.post<ApiResponse<Particular>>(
      API_PATHS.finance.createParticular,
      normalizeParticularPayload(particularData),
    )
    return data.data
  },

  updateParticular: async (id: string, particularData: Partial<Particular>) => {
    const { data } = await api.patch<ApiResponse<Particular>>(
      API_PATHS.finance.updateParticular(id),
      normalizeParticularPayload(particularData),
    )
    return data.data
  },

  deleteParticular: async (id: string) => {
    await api.delete(API_PATHS.finance.deleteParticular(id))
  },

  markParticularRequested: async (id: string) => {
    const { data } = await api.post<ApiResponse<Particular>>(API_PATHS.finance.markParticularRequested(id), {})
    return data.data
  },

  markParticularOrderPlaced: async (
    id: string,
    payload: {
      supplier: string | number
      expected_arrival_date: string
      ordered_quantity?: number | string
      purchase_order_ref?: string
    },
  ) => {
    const { data } = await api.post<ApiResponse<Particular>>(API_PATHS.finance.markParticularOrderPlaced(id), payload)
    return data.data
  },

  markParticularArrived: async (
    id: string,
    payload: { in_stock?: boolean; received_quantity?: number | string } = {},
  ) => {
    const { data } = await api.post<ApiResponse<Particular>>(API_PATHS.finance.markParticularArrived(id), payload)
    return data.data
  },

  approveParticularPurchase: async (id: string, payload: { in_stock?: boolean; create_expense?: boolean } = {}) => {
    const { data } = await api.post<ApiResponse<Particular>>(API_PATHS.finance.approveParticularPurchase(id), payload)
    return data.data
  },

  rejectParticularPurchase: async (id: string, rejection_reason: string) => {
    const { data } = await api.post<ApiResponse<Particular>>(API_PATHS.finance.rejectParticularPurchase(id), { rejection_reason })
    return data.data
  },

  setParticularInStock: async (id: string, in_stock: boolean) => {
    const { data } = await api.post<ApiResponse<Particular>>(API_PATHS.finance.setParticularInStock(id), { in_stock })
    return data.data
  },

  getParticularStatusTimeline: async (id: string) => {
    const { data } = await api.get<ApiResponse<ParticularStatusEvent[]> | ParticularStatusEvent[]>(
      API_PATHS.finance.particularStatusTimeline(id),
    )
    return unwrapPayload<ParticularStatusEvent[]>(data) ?? []
  },

  // Invoice Preview
  getInvoicePreview: async (jobId: string, service_instance_id?: string | number | null) => {
    const params = new URLSearchParams()
    if (service_instance_id != null && String(service_instance_id).trim()) {
      params.append('service_instance_id', String(service_instance_id))
    }

    const { data } = await api.get<ApiResponse<InvoicePreview> | InvoicePreview>(API_PATHS.finance.invoicePreview(jobId), { params })
    return unwrapPayload<InvoicePreview>(data)
  },

  getInvoices: async (filters: { job_id?: string; status?: string; paid_status?: string; pay_status?: string; page?: number; page_size?: number; service_instance_id?: string | number } = {}) => {
    const params = new URLSearchParams()
    if (filters.job_id) params.append('job', filters.job_id)
    if (filters.status) params.append('status', filters.status)
    if (filters.paid_status) params.append('paid_status', filters.paid_status)
    if (filters.pay_status) params.append('pay_status', filters.pay_status)
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }

    const { data } = await api.get<ApiResponse<PaginatedResponse<JobInvoice>> | PaginatedResponse<JobInvoice>>(API_PATHS.finance.invoices, { params })
    return normalizePaginated<JobInvoice>(data)
  },

  createInvoice: async (invoiceData: { job: string | number; due_date?: string; notes?: string }) => {
    const { data } = await api.post<ApiResponse<JobInvoice> | JobInvoice>(API_PATHS.finance.createInvoice, invoiceData)
    return unwrapPayload<JobInvoice>(data)
  },

  getInvoice: async (invoiceId: string | number, service_instance_id?: string | number | null) => {
    const params = new URLSearchParams()
    if (service_instance_id != null && String(service_instance_id).trim()) {
      params.append('service_instance_id', String(service_instance_id))
    }

    const { data } = await api.get<ApiResponse<JobInvoice> | JobInvoice>(API_PATHS.finance.invoiceDetail(String(invoiceId)), { params })
    return unwrapPayload<JobInvoice>(data)
  },

  issueInvoice: async (invoiceId: string | number) => {
    const { data } = await api.post<ApiResponse<JobInvoice> | JobInvoice>(API_PATHS.finance.issueInvoice(String(invoiceId)))
    return unwrapPayload<JobInvoice>(data)
  },

  resendInvoice: async (invoiceId: string | number) => {
    const { data } = await api.post<ApiResponse<JobInvoice> | JobInvoice>(API_PATHS.finance.resendInvoice(String(invoiceId)))
    return unwrapPayload<JobInvoice>(data)
  },

  markInvoicePaid: async (invoiceId: string | number) => {
    const { data } = await api.post<ApiResponse<JobInvoice> | JobInvoice>(API_PATHS.finance.markInvoicePaid(String(invoiceId)))
    return unwrapPayload<JobInvoice>(data)
  },

  voidInvoice: async (invoiceId: string | number) => {
    const { data } = await api.post<ApiResponse<JobInvoice> | JobInvoice>(API_PATHS.finance.voidInvoice(String(invoiceId)))
    return unwrapPayload<JobInvoice>(data)
  },

  getInvoicePayments: async (filters: { invoice_id?: string | number; page?: number; page_size?: number; service_instance_id?: string | number } = {}) => {
    const params = new URLSearchParams()
    if (filters.invoice_id !== undefined) params.append('invoice', String(filters.invoice_id))
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }

    const { data } = await api.get<ApiResponse<PaginatedResponse<JobInvoicePayment>> | PaginatedResponse<JobInvoicePayment>>(API_PATHS.finance.invoicePayments, { params })
    return normalizePaginated<JobInvoicePayment>(data)
  },

  getJobPayments: async (filters: {
    job_id?: string | number
    invoice_id?: string | number | null
    payment_stage?: 'advance' | 'invoice_payment'
    page?: number
    page_size?: number
    service_instance_id?: string | number
  } = {}) => {
    const params = new URLSearchParams()
    if (filters.job_id !== undefined) params.append('job', String(filters.job_id))
    if (filters.invoice_id !== undefined && filters.invoice_id !== null) params.append('invoice', String(filters.invoice_id))
    if (filters.payment_stage) params.append('payment_stage', filters.payment_stage)
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }

    const { data } = await api.get<ApiResponse<PaginatedResponse<JobInvoicePayment>> | PaginatedResponse<JobInvoicePayment>>(API_PATHS.finance.jobPayments, { params })
    return normalizePaginated<JobInvoicePayment>(data)
  },

  createInvoicePayment: async (payload: {
    invoice: string | number
    amount: number | string
    payment_date?: string
    payment_method?: string
    reference?: string
    notes?: string
  }) => {
    const normalizedPayload = {
      ...payload,
      payment_stage: 'invoice_payment',
      payment_method_id: payload.payment_method,
    }
    const { data } = await api.post<ApiResponse<JobInvoicePayment> | JobInvoicePayment>(API_PATHS.finance.createInvoicePayment, normalizedPayload)
    return unwrapPayload<JobInvoicePayment>(data)
  },

  createJobPayment: async (payload: {
    job: string | number
    invoice?: string | number | null
    amount: number | string
    payment_date?: string
    payment_method_id?: string | number | null
    payment_stage?: 'advance' | 'invoice_payment'
    paid_status?: 'paid' | 'unpaid'
    reference?: string
    notes?: string
  }) => {
    const { data } = await api.post<ApiResponse<JobInvoicePayment> | JobInvoicePayment>(API_PATHS.finance.createJobPayment, payload)
    return unwrapPayload<JobInvoicePayment>(data)
  },

  deleteInvoicePayment: async (id: string | number) => {
    await api.delete(API_PATHS.finance.deleteInvoicePayment(String(id)))
  },

  deleteJobPayment: async (id: string | number) => {
    await api.delete(API_PATHS.finance.deleteJobPayment(String(id)))
  },

  getPublicInvoicePaymentDetail: async (jobId: string | number, invoiceId: string | number, token?: string | null) => {
    const { data } = await api.get<JobInvoice>(
      API_PATHS.finance.publicInvoicePaymentDetail(jobId, invoiceId),
      {
        params: token ? { token } : undefined,
        skipAuthRedirectOn401: true,
        skipPostIdHeader: true,
        skipServiceInstanceId: true,
      } as any,
    )
    return data
  },

  initiateInvoiceGatewayPayment: async (jobId: string | number, invoiceId: string | number, token?: string | null) => {
    const { data } = await api.post<{
      invoice: JobInvoice
      wallet?: { id?: string | number; name?: string | null; gateway?: string | null; gateway_code?: number | string | null }
      amount?: number | string
      payment_gateway?: number | string
      payment_link?: string | null
      payment_reference_id?: string | null
      publishable_key?: string | null
      signature?: string | null
      gateway_response?: unknown
      detail?: string
    }>(
      API_PATHS.finance.initiateInvoicePayment(jobId, invoiceId),
      token ? { token } : {},
      { skipAuthRedirectOn401: true, skipPostIdHeader: true, skipServiceInstanceId: true } as any,
    )
    return data
  },

  confirmInvoiceStripePayment: async (
    jobId: string | number,
    invoiceId: string | number,
    payload: { payment_reference_id: string; token?: string | null; wallet_id?: string | number | null },
  ) => {
    const { data } = await api.post<{
      invoice: JobInvoice
      payment?: JobInvoicePayment
      paid?: boolean
      detail?: string
    }>(
      API_PATHS.finance.confirmInvoiceStripePayment(jobId, invoiceId),
      payload,
      { skipAuthRedirectOn401: true, skipPostIdHeader: true, skipServiceInstanceId: true } as any,
    )
    return data
  },

  getPublicInvoicePaymentTokenDetail: async (token: string) => {
    const { data } = await api.get<{
      type?: 'invoice'
      invoice?: JobInvoice
      job?: Record<string, unknown>
      amount_due?: string | number
      can_pay?: boolean
      paid?: boolean
      message?: string
      payment_reference?: string | null
    }>(
      API_PATHS.finance.publicInvoicePaymentTokenDetail(token),
      publicPaymentRequestOptions,
    )
    return data
  },

  getPublicServiceInstanceBranding: async (serviceInstanceId: string | number) => {
    const { data } = await api.get<{
      results?: Array<Record<string, unknown>>
      data?: { results?: Array<Record<string, unknown>> }
    }>(
      '/Org/Public/Services/ServiceInstances/List/',
      {
        params: {
          service_instance_id: serviceInstanceId,
          address: true,
          settings: true,
          page_size: 1,
        },
        ...publicPaymentRequestOptions,
      } as any,
    )

    if (Array.isArray(data?.results) && data.results.length > 0) {
      return data.results[0]
    }
    if (Array.isArray(data?.data?.results) && data.data.results.length > 0) {
      return data.data.results[0]
    }
    return null
  },

  initiateInvoiceGatewayPaymentByToken: async (token: string) => {
    const { data } = await api.post<{
      invoice: JobInvoice
      wallet?: { id?: string | number; name?: string | null; gateway?: string | null; gateway_code?: number | string | null }
      amount?: number | string
      payment_gateway?: number | string
      payment_link?: string | null
      payment_reference_id?: string | null
      publishable_key?: string | null
      signature?: string | null
      gateway_response?: unknown
      detail?: string
    }>(
      API_PATHS.finance.initiateInvoiceTokenPayment(token),
      {},
      publicPaymentRequestOptions,
    )
    return data
  },

  confirmInvoiceStripePaymentByToken: async (
    token: string,
    payload: { payment_reference_id: string; wallet_id?: string | number | null },
  ) => {
    const { data } = await api.post<{
      invoice: JobInvoice
      payment?: JobInvoicePayment
      paid?: boolean
      awaiting_webhook?: boolean
      detail?: string
    }>(
      API_PATHS.finance.confirmInvoiceTokenStripePayment(token),
      payload,
      publicPaymentRequestOptions,
    )
    return data
  },

  // Worker Payments Summary
  getWorkerPaymentsSummary: async (filters: FinanceFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.invoice_id) params.append('invoice_id', String(filters.invoice_id))
    if (filters.worker_id) params.append('worker_id', filters.worker_id)
    if (filters.search) params.append('search', filters.search)
    if (filters.payment_status) params.append('payment_status', filters.payment_status)
    if (filters.date_from) params.append('date_from', filters.date_from)
    if (filters.date_to) params.append('date_to', filters.date_to)
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }
    
    const { data } = await api.get(API_PATHS.finance.workerPaymentsSummary, { params })
    return 'data' in data ? data.data : data
  },

  getWorkerPaymentPeriods: async (filters: FinanceFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.invoice_id) params.append('invoice_id', String(filters.invoice_id))
    if (filters.worker_id) params.append('worker_id', filters.worker_id)
    if (filters.search) params.append('search', filters.search)
    if (filters.payment_status) params.append('payment_status', filters.payment_status)
    if (filters.date_from) params.append('date_from', filters.date_from)
    if (filters.date_to) params.append('date_to', filters.date_to)
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }

    const { data } = await api.get(API_PATHS.finance.workerPaymentPeriods, { params })
    return 'data' in data ? data.data : data
  },

  getWorkerInvoiceGenerationRuns: async (filters: Pick<FinanceFilters, 'service_instance_id'> & { limit?: number } = {}) => {
    const params = new URLSearchParams()
    if (filters.limit) params.append('limit', String(filters.limit))
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }

    const { data } = await api.get(API_PATHS.finance.workerInvoiceGenerationRuns, { params })
    const payload = 'data' in data ? data.data : data
    return (payload?.results ?? []) as WorkerInvoiceGenerationRun[]
  },

  getWorkerDraftInvoice: async (filters: FinanceFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }
    if (filters.job_id) params.append('job_id', filters.job_id)
    if (filters.worker_id) params.append('worker_id', filters.worker_id)
    if (filters.assignment_id) params.append('assignment_id', filters.assignment_id)
    if (filters.date_from) params.append('date_from', filters.date_from)
    if (filters.date_to) params.append('date_to', filters.date_to)

    const { data } = await api.get(API_PATHS.finance.workerDraftInvoice, { params })
    return 'data' in data ? data.data : data
  },

  recordWorkerPayment: async (paymentData: WorkerPaymentRecordPayload) => {
    const { data } = await api.post(API_PATHS.finance.recordWorkerPayment, paymentData)
    return 'data' in data ? data.data : data
  },

  updateWorkerPayment: async (paymentId: string | number, paymentData: Omit<WorkerPaymentRecordPayload, 'invoice_id'>) => {
    const { data } = await api.post(API_PATHS.finance.updateWorkerPayment(paymentId), paymentData)
    return 'data' in data ? data.data : data
  },

  deactivateWorkerPayment: async (paymentId: string | number) => {
    const { data } = await api.post(API_PATHS.finance.deactivateWorkerPayment(paymentId), {})
    return 'data' in data ? data.data : data
  },

  deactivateWorkerPaymentInvoice: async (invoiceId: string | number) => {
    const { data } = await api.post(API_PATHS.finance.deactivateWorkerPaymentInvoice(invoiceId), {})
    return 'data' in data ? data.data : data
  },

  resendWorkerPaymentInvoice: async (invoiceId: string | number) => {
    const { data } = await api.post(API_PATHS.finance.resendWorkerPaymentInvoice(invoiceId), {})
    return 'data' in data ? data.data : data
  },

  generateWorkerDailyInvoices: async (
    payload: { date_from: string; date_to: string; worker_id?: string | number | null },
    serviceInstanceId?: string | number | null,
  ) => {
    const params = new URLSearchParams()
    if (serviceInstanceId != null && String(serviceInstanceId).trim()) {
      params.append('service_instance_id', String(serviceInstanceId))
    }
    const { data } = await api.post(
      `${API_PATHS.finance.generateWorkerDailyInvoices}${params.toString() ? `?${params}` : ''}`,
      { ...payload, worker_id: payload.worker_id ?? undefined },
    )
    return 'data' in data ? data.data : data
  },

  getWorkerDailyInvoiceList: async (filters: FinanceFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.worker_id) params.append('worker_id', filters.worker_id)
    if (filters.search) params.append('search', filters.search)
    if (filters.payment_status) params.append('payment_status', filters.payment_status)
    if (filters.date_from) params.append('date_from', filters.date_from)
    if (filters.date_to) params.append('date_to', filters.date_to)
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }
    const { data } = await api.get(API_PATHS.finance.workerDailyInvoiceList, { params })
    return 'data' in data ? data.data : data
  },

  getWorkerDailyInvoiceDetail: async (invoiceId: string | number, serviceInstanceId?: string | number | null) => {
    const params = new URLSearchParams()
    if (serviceInstanceId != null && String(serviceInstanceId).trim()) {
      params.append('service_instance_id', String(serviceInstanceId))
    }
    const { data } = await api.get(API_PATHS.finance.workerDailyInvoiceDetail(invoiceId), { params })
    return 'data' in data ? data.data : data
  },

  getCompanyPaymentsSummary: async (filters: FinanceFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.worker_id) params.append('worker_id', filters.worker_id)
    if (filters.search) params.append('search', filters.search)
    if (filters.payment_status) params.append('payment_status', filters.payment_status)
    if (filters.date_from) params.append('date_from', filters.date_from)
    if (filters.date_to) params.append('date_to', filters.date_to)

    const { data } = await api.get(API_PATHS.finance.companyPaymentsSummary, { params })
    return 'data' in data ? data.data : data
  },

  getCompanyPaymentJobs: async (filters: FinanceFilters = {}) => {
    const params = new URLSearchParams()
    if (filters.worker_id) params.append('worker_id', filters.worker_id)
    if (filters.date_from) params.append('date_from', filters.date_from)
    if (filters.date_to) params.append('date_to', filters.date_to)

    const { data } = await api.get(API_PATHS.finance.companyPaymentJobs, { params })
    return 'data' in data ? data.data : data
  },

  recordCompanyPayment: async (paymentData: CompanyPaymentRecordPayload) => {
    const { data } = await api.post(API_PATHS.finance.recordCompanyPayment, paymentData)
    return 'data' in data ? data.data : data
  },

  getSettlementInvoices: async (filters: {
    page?: number
    page_size?: number
    counterparty_instance_id?: string | number
    status?: number
    service_instance_id?: string | number
  } = {}) => {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.counterparty_instance_id) params.append('counterparty_instance_id', String(filters.counterparty_instance_id))
    if (filters.status !== undefined) params.append('status', String(filters.status))
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }

    const { data } = await api.get(API_PATHS.finance.settlementInvoicesList, { params })
    return 'data' in data ? data.data : data
  },

  generateSettlementInvoices: async (payload: { from_date: string; to_date: string }) => {
    const { data } = await api.post(API_PATHS.finance.settlementInvoicesGenerate, payload)
    return 'data' in data ? data.data : data
  },

  recordSettlementPayment: async (payload: {
    settlement_invoice_id: string | number
    amount: number
    src_wallet_id?: string | number | null
    dest_wallet_id?: string | number | null
    purpose?: 'settlement' | 'commission'
    payment_ref?: string
    notes?: string
    service_instance_id?: string | number
  }) => {
    const params = new URLSearchParams()
    if (payload.service_instance_id != null && String(payload.service_instance_id).trim()) {
      params.append('service_instance_id', String(payload.service_instance_id))
    }
    const { settlement_invoice_id, service_instance_id: _si, ...body } = payload
    const { data } = await api.post(
      API_PATHS.finance.recordSettlementPayment,
      { settlement_invoice_id, ...body },
      { params },
    )
    return 'data' in data ? data.data : data
  },

  getJobPaymentSummary: async (jobId: string | number) => {
    const { data } = await api.get(API_PATHS.finance.jobPaymentSummary(jobId))
    return unwrapPayload<JobPaymentSummary>(data)
  },

  getOrderPaymentSummary: async (orderId: string | number) => {
    const { data } = await api.get(API_PATHS.finance.orderPaymentSummary(orderId))
    return unwrapPayload<OrderPaymentSummary>(data)
  },

  getNotificationEvents: async () => {
    const { data } = await api.get(API_PATHS.finance.notificationEventsList)
    return unwrapPayload<NotificationEventsCatalog>(data)
  },

  getNotificationContacts: async (filters: {
    service_instance_id?: string | number
    notification_key?: string
  } = {}) => {
    const params = new URLSearchParams()
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }
    if (filters.notification_key) params.append('notification_key', filters.notification_key)
    const { data } = await api.get(API_PATHS.finance.notificationContactsList, { params })
    return unwrapPayload<NotificationContactsResponse>(data)
  },

  addNotificationContact: async (payload: NotificationContactPayload) => {
    const params = new URLSearchParams()
    if (payload.service_instance_id != null && String(payload.service_instance_id).trim()) {
      params.append('service_instance_id', String(payload.service_instance_id))
    }
    const { service_instance_id: _si, ...body } = payload
    const { data } = await api.post(API_PATHS.finance.notificationContactsAdd, body, { params })
    return unwrapPayload<NotificationContactRow>(data)
  },

  updateNotificationContact: async (contactId: string | number, payload: Partial<NotificationContactPayload>) => {
    const { data } = await api.patch(API_PATHS.finance.notificationContactUpdate(contactId), payload)
    return unwrapPayload<NotificationContactRow>(data)
  },

  removeNotificationContact: async (contactId: string | number) => {
    const { data } = await api.delete(API_PATHS.finance.notificationContactRemove(contactId))
    return unwrapPayload<unknown>(data)
  },

  getNotificationDeliveryLogs: async (filters: {
    service_instance_id?: string | number
    notification_key?: string
    status?: string
    run_id?: string
    page?: number
    page_size?: number
  } = {}) => {
    const params = new URLSearchParams()
    if (filters.service_instance_id != null && String(filters.service_instance_id).trim()) {
      params.append('service_instance_id', String(filters.service_instance_id))
    }
    if (filters.notification_key) params.append('notification_key', filters.notification_key)
    if (filters.status) params.append('status', filters.status)
    if (filters.run_id) params.append('run_id', filters.run_id)
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    const { data } = await api.get(API_PATHS.finance.notificationDeliveryLogsList, { params })
    return unwrapPayload<NotificationDeliveryLogsResponse>(data)
  },
}

export type JobPaymentSummary = {
  job_id: number
  job_ref?: string | null
  job_title?: string | null
  job_status?: string | number | null
  order_id?: number | null
  order_company_instance_id?: number | null
  job_company_instance_id?: number | null
  order_total: number
  job_pay: number
  commission_amount: number
  prepaid_amount: number
  order_payments_total: number
  collectable_amount: number
  cod_collected_total: number
  settlement_due: number
  collection_mode: string
  settlement_direction: string
  ledger_entries: Array<{
    id: number
    from_instance_id: number
    to_instance_id: number
    amount: number
    purpose: string
    accrued_at?: string | null
    settlement_invoice_id?: number | null
  }>
}

export type OrderPaymentSummary = {
  order_id: number
  jobs_count: number
  order_payments_total: number
  order_company_instance_id?: number | null
  job_company_instance_id?: number | null
  totals: {
    order_total: number
    job_pay: number
    commission_amount: number
    collectable_amount: number
    cod_collected_total: number
    settlement_due: number
  }
  jobs: JobPaymentSummary[]
}

export type SettlementInvoiceRow = {
  id: number
  from_instance_id: number
  to_instance_id: number
  start_date?: string | null
  end_date?: string | null
  period_amount: number
  previous_balance: number
  overall_pending_amount: number
  amount_paid: number
  jobs_count: number
  status: number
  closed_on?: string | null
}

export type NotificationEventOption = {
  key: string
  text: string
  description?: string
  channels?: string[]
}

export type NotificationEventsCatalog = {
  name: string
  list: NotificationEventOption[]
}

export type NotificationContactRow = {
  id: number
  service_instance_id: number
  label?: string
  notification_key: string
  notification_label?: string
  email?: string
  phone?: string
  whatsapp?: string
  notify_email: boolean
  notify_sms: boolean
  notify_whatsapp: boolean
  notify_push: boolean
  active: boolean
  created_on?: string
  updated_on?: string
}

export type NotificationContactsResponse = {
  results: NotificationContactRow[]
}

export type NotificationContactPayload = {
  service_instance_id?: string | number
  label?: string
  notification_key: string
  email?: string
  phone?: string
  whatsapp?: string
  notify_email?: boolean
  notify_sms?: boolean
  notify_whatsapp?: boolean
  notify_push?: boolean
  active?: boolean
}

export type NotificationDeliveryLogRow = {
  id: number
  service_instance_id: number
  notification_contact_id?: number | null
  contact_label?: string | null
  notification_key: string
  notification_label?: string
  channel: string
  recipient?: string
  status: 'sent' | 'failed' | 'skipped'
  error_message?: string
  subject?: string
  body_preview?: string
  settlement_invoice_id?: number | null
  worker_payment_invoice_id?: number | null
  trigger_source: string
  run_id: string
  run_at: string
  sent_at: string
}

export type NotificationDeliveryLogsResponse = {
  count: number
  page: number
  page_size: number
  results: NotificationDeliveryLogRow[]
}
